'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { Info, Map, Search, Wifi, X } from 'lucide-react'
import Flag from '@/components/ui/flag'
import { AnimatedBorderButton, StaticBorderButton } from '@/components/snippets/buttons'
import { PriceDisplay } from './esim-public-utils'
import { PublicPlan, NetworkOperator, RegionCountry } from '@/types/esim'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'

export type PlanCardProps = {
  plan: PublicPlan
  buttonVariant?: 'static' | 'animated'
  buttonLabel?: string
  className?: string
  onChoose?: () => void
}

export function PlanCard({
  plan,
  buttonVariant = 'static',
  buttonLabel = 'Choose Plan',
  className,
  onChoose,
}: PlanCardProps) {
  const router = useRouter()
  const gb = parseFloat(plan.data_quota_gb)
  const isUnlimited = plan.data_plan_type === 'unlimitedStandard'
  const isRegion = plan.plan_type === 'region'

  const regionCountries: RegionCountry[] = isRegion && Array.isArray(plan.countries)
    ? plan.countries.filter((c): c is RegionCountry => typeof c === 'object' && c !== null && 'iso2' in c)
    : []


  const handleChoose = () => {
    if (onChoose) {
      onChoose()
      return
    }
    router.push(`/public-esim/checkout?slug=${plan.slug}`)
  }

  const ActionButton = buttonVariant === 'animated' ? AnimatedBorderButton : StaticBorderButton

  return (
    <div
      onClick={handleChoose}
      className={cn(
        "group relative flex flex-col rounded-3xl border border-brand-stroke/30 bg-brand-card/90 overflow-hidden shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_45px_rgba(0,0,0,0.08)] cursor-pointer",
        className
      )}
    >
      <div className="p-5 flex flex-col gap-4 flex-1">
        <div className="flex items-start justify-center">
          <div className="flex items-center gap-3 min-w-0">
            {plan.iso2 ? (
              <div className="flex h-10 items-center justify-center">
                  <Flag code={plan.iso2.toLowerCase()} width={28} height={28} circle={true} />
              </div>
            ) : (
              <div className="flex h-10 w-12 items-center justify-center ">
                <Map className="h-4 w-4 text-brand-muted" />
              </div>
            )}
            <div className="min-w-0 flex justify-between items-start gap-2 flex-col">
              <p className="text-base font-semibold text-brand-text leading-tight 3xl:text-2xl">
                {plan.country || plan.region}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="global-border-radius border border-brand-stroke/20 bg-brand-surface/70 px-3 py-4 text-center">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-muted 3xl:text-xl">Data</p>
            <p className="mt-2 text-sm font-semibold text-brand-text 3xl:text-xl">
              {isUnlimited ? 'Unlimited' : `${gb % 1 === 0 ? gb.toFixed(0) : gb} GB`}
            </p>
          </div>
          <div className="global-border-radius border border-brand-stroke/20 bg-brand-surface/70 px-3 py-4 text-center">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-muted 3xl:text-xl">Validity</p>
            <p className="mt-2 text-sm font-semibold text-brand-text 3xl:text-xl">{plan.validity_days} days</p>
          </div>
        </div>

        {isRegion && regionCountries.length > 0 && (
          <div className="flex items-center justify-center gap-1.5">
            <p className="text-xs leading-relaxed text-brand-muted text-center 3xl:text-xl">
              {regionCountries.length} Countries
            </p>
            <div onClick={(e) => e.stopPropagation()}>
              <CountriesInfoDialog countries={regionCountries} regionName={plan.region || ''} />
            </div>
          </div>
        )}

        {!isRegion && plan.network_operators && plan.network_operators.length > 0 && (
          <NetworkOperatorsButton
            operators={plan.network_operators}
            planName={plan.country || plan.region || ''}
          />
        )}
      </div>

      <div className="px-5 pb-5 pt-3 flex flex-col gap-3">
        <div className="text-sm font-semibold text-brand-text 3xl:text-base">
          <div className="mt-1">
            <PriceDisplay plan={plan} size="md" align="start" />
          </div>
        </div>
        <div onClick={(event) => event.stopPropagation()}>
          <ActionButton type="button" onClick={handleChoose}>
            <Wifi className="h-3.5 w-3.5" />
            {buttonLabel}
          </ActionButton>
        </div>
      </div>
    </div>
  )
}

function CountriesInfoDialog({ countries, regionName }: { countries: RegionCountry[]; regionName: string }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const filtered = search.trim()
    ? countries.filter((c) => c.name.toLowerCase().includes(search.trim().toLowerCase()))
    : countries

  return (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation()
          setSearch('')
          setOpen(true)
        }}
        className="flex-shrink-0 text-brand-muted hover:text-brand-text transition-colors"
        aria-label={`View ${countries.length} covered countries`}
      >
        <Info className="h-3.5 w-3.5 3xl:h-5 3xl:w-5" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          showCloseButton={false}
          className="max-w-xs sm:max-w-sm 3xl:max-w-2xl bg-brand-card border-brand-stroke/20 rounded-3xl p-0 gap-0 overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between px-6 pt-6 pb-5 border-b border-brand-stroke/15 bg-brand-surface/40">
            <div className="flex flex-col gap-1">
              <p className="text-base font-semibold uppercase tracking-widest text-brand-muted 3xl:text-2xl">
                {countries.length} Countries Covered
              </p>
              <DialogTitle className="text-base font-semibold text-brand-text leading-tight 3xl:text-2xl">
                {regionName}
              </DialogTitle>
            </div>
            <DialogClose className="mt-0.5 rounded-full p-1.5 text-brand-muted hover:text-brand-text hover:bg-brand-stroke/20 transition-colors">
              <X className="h-4 w-4 3xl:h-5 3xl:w-5" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>

          {/* Search */}
          <div className="px-6 py-3 border-b border-brand-stroke/10">
            <div className="flex items-center gap-2 bg-brand-surface rounded-full px-3 py-2">
              <Search className="h-3.5 w-3.5 text-brand-muted shrink-0" />
              <input
                autoFocus
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search countries..."
                className="flex-1 bg-transparent text-xs text-brand-text placeholder:text-brand-muted outline-none 3xl:text-base"
              />
              {search && (
                <button type="button" onClick={() => setSearch('')}>
                  <X className="h-3 w-3 text-brand-muted hover:text-brand-text transition-colors" />
                </button>
              )}
            </div>
          </div>

          {/* Country list */}
          <div className="flex flex-col max-h-[50vh] overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="py-8 text-xs text-center text-brand-muted">No countries found.</p>
            ) : (
              filtered.map((country) => (
                <div
                  key={country.iso2}
                  className="flex items-center gap-3 px-6 py-2.5 border-b border-brand-stroke/10 last:border-0"
                >
                  <div className="flex items-center justify-center overflow-hidden rounded h-4 w-6 shrink-0">
                    <Flag code={country.iso2.toLowerCase()} width={22} height={22} circle={true} />
                  </div>
                  <span className="text-xs text-brand-text/80 3xl:text-base">{country.name}</span>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-3.5 bg-brand-surface/30 border-t border-brand-stroke/10">
            <p className="text-[10px] text-brand-muted/50 3xl:text-sm">
              {search ? `${filtered.length} of ${countries.length} countries` : `${countries.length} countries total`}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

const NETWORK_BADGES = [
  { label: '3G', key: 'supports_3g' as const },
  { label: '4G LTE', key: 'supports_4g_lte' as const },
  { label: '5G', key: 'supports_5g' as const },
]

function NetworkOperatorsButton({ operators, planName }: { operators: NetworkOperator[]; planName: string }) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <div
        className="flex items-center justify-center gap-1.5"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-xs leading-relaxed text-brand-muted text-center 3xl:text-xl">
          {operators.length} {operators.length === 1 ? 'Provider' : 'Providers'}
        </p>
        <button
          onClick={(e) => {
            e.stopPropagation()
            setOpen(true)
          }}
          className="flex-shrink-0 text-brand-muted hover:text-brand-text transition-colors"
          aria-label={`View network providers for ${planName}`}
        >
          <Info className="h-3.5 w-3.5 3xl:h-5 3xl:w-5" />
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          showCloseButton={false}
          className="max-w-xs sm:max-w-sm 3xl:max-w-2xl bg-brand-card border-brand-stroke/20 rounded-3xl p-0 gap-0 overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between px-6 pt-6 pb-5 border-b border-brand-stroke/15 bg-brand-surface/40">
            <div className="flex flex-col gap-1">
              <p className="text-base font-semibold uppercase tracking-widest text-brand-muted 3xl:text-2xl">
                Available Networks
              </p>
              <DialogTitle className="text-base font-semibold text-brand-text leading-tight 3xl:text-2xl">
                {planName}
              </DialogTitle>
            </div>
            <DialogClose className="mt-0.5 rounded-full p-1.5 text-brand-muted hover:text-brand-text hover:bg-brand-stroke/20 transition-colors">
              <X className="h-4 w-4 3xl:h-5 3xl:w-5" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>

          {/* Operators list */}
          <div className="px-6 py-2 flex flex-col max-h-[60vh] overflow-y-auto">
            {operators.map((op) => (
              <div
                key={op.network_name}
                className="flex items-center justify-between gap-3 py-3.5 border-b border-brand-stroke/10 last:border-0"
              >
                <span className="text-sm font-medium text-brand-text/80 3xl:text-xl">{op.network_name}</span>
                <div className="flex items-center gap-1.5 shrink-0">
                  {NETWORK_BADGES.map((b) =>
                    op[b.key] ? (
                      <span key={b.label} className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-brand-green/10 border border-brand-green/20 text-brand-green 3xl:text-base">
                        {b.label}
                      </span>
                    ) : (
                      <span key={b.label} className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-brand-stroke/10 text-brand-muted/50 line-through 3xl:text-base">
                        {b.label}
                      </span>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="px-6 py-3.5 bg-brand-surface/30 border-t border-brand-stroke/10">
            <p className="text-[10px] text-brand-muted/50 3xl:text-sm">Coverage may vary by location and device.</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export function PlanCardSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("rounded-3xl border border-brand-stroke/30 bg-brand-card/90 p-5 flex flex-col gap-4 animate-pulse shadow-sm", className)}>
      <div className="flex items-start gap-3">
        <div className="h-10 w-12 global-border-radius bg-brand-surface/70" />
        <div className="flex-1 space-y-2 py-1">
          <div className="h-4 w-32 rounded-full bg-brand-surface/70" />
          <div className="h-3 w-20 rounded-full bg-brand-surface/70" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="h-16 global-border-radius bg-brand-surface/70" />
        <div className="h-16 global-border-radius bg-brand-surface/70" />
      </div>
      <div className="h-3 w-24 rounded-full bg-brand-surface/70" />
      <div className="flex items-center justify-between gap-4">
        <div className="h-4 w-20 rounded-full bg-brand-surface/70" />
        <div className="h-10 w-32 global-border-radius bg-brand-surface/70" />
      </div>
    </div>
  )
}
